import { Avatar, Card, Tag } from 'antd'
import { SvgEmailIcon, SvgNoteIcon, SvgTextIcon } from 'components/icons'
import { CallRow, TCall } from 'features/calls'
import { TMainActivityTypes, TUserAllActivity } from 'features/leads/types'
import { FC, ReactNode, useState } from 'react'
import { formatDate } from 'utils/functions'
import { useLeadDetails } from 'features/leads/providers/LeadDetailsProvider'
import useAuth from 'hooks/useAuth'
import Utils from 'utils/helper'
import { buildDetailModal } from './ActivityDetailModal'
import ActivityEmailActions from './ActivityEmailActions'
import ActivityNoteActions from './ActivityNoteActions'

const CommonItemCard: FC<{
 refechList?: () => void
 fetchDetail?: () => void
 data?: TUserAllActivity | any
 rowData?: any
 type?: TMainActivityTypes
 loading?: boolean
 ItemcardDetail?: {
  titleNode?: ReactNode
  title?: string
  description?: string
  updated?: string
  estatus?: any
  createdBy?: string
 }
 from?: 'email'
 onEditNote?: (item: any) => void
}> = ({
 data,
 ItemcardDetail,
 fetchDetail,
 refechList,
 loading,
 type,
 from,
 rowData,
 onEditNote,
}) => {
 const { Meta } = Card
 const [showModal, setShowModal] = useState(false)
 const { isTransferred, leadId } = useLeadDetails()
 const { user } = useAuth()

 // Same rule as ActivityNoteActions: editable only when it's a transfer lead
 // and the current user owns the note; everything else is read-only
 const isReadOnlyNote =
  type === 'note' &&
  !(
   isTransferred === true &&
   rowData?.create_by_user_id != null &&
   String(rowData.create_by_user_id) === String(user?.id)
  )

 const setAvtIcon = () => {
  switch (type) {
   case 'email':
    return <SvgEmailIcon />
   case 'call':
    return <i className="fas fa-phone-alt" style={{ color: 'black' }} />
   case 'note':
    return <SvgNoteIcon />
   case 'text':
    return <SvgTextIcon />
   case 'web':
    return <i className="fas fa-solid fa-globe" style={{ color: 'black' }} />
   default:
    return null
  }
 }

 // Voice calls in the merged "ALL" feed must look and behave identically to
 // the dedicated Call Log tab - delegate to the same CallRow component
 // instead of maintaining a second copy of its card/expand/recording logic.
 if (type === 'voicecall') {
  // The per-row fields (direction/from_number/to_number/duration/
  // has_recording/status/contact_name) arrive via `rowData`, not `data` -
  // `data` is the shared, lazily-fetched detail object used only by the
  // click-to-open-modal flow the other activity types use, so it's stale/
  // empty here until some other row's modal has been opened.
  const call: TCall = {
   id: rowData?.activity_id,
   direction: rowData?.direction,
   from_number: rowData?.from_number,
   to_number: rowData?.to_number,
   contact_name: rowData?.contact_name,
   duration: rowData?.duration,
   has_recording: rowData?.has_recording,
   status: rowData?.status,
   created_at: rowData?.created_at ?? rowData?.created ?? rowData?.updated,
  }
  return <CallRow call={call} leadId={leadId} />
 }

 return (
  <>
   <Card
    hoverable
    onClick={() => {
     setShowModal(true)
     fetchDetail?.()
    }}
    style={{ borderColor: '#ccc' }}
   >
    <div className="d-flex justify-content-between">
     <div>
      <Meta
       avatar={
        <Avatar
         //  style={{ backgroundColor: '#eff6fc' }}
         size={50}
         icon={setAvtIcon()}
         // Shared class sizes every timeline icon uniformly (see _lead-styles.scss);
         // color is needed because SvgTextIcon uses currentcolor
         className="timeline-avatar-icon"
         style={{ color: '#2a2a2a' }}
        />
       }
       description={
        <div>
         {ItemcardDetail?.titleNode ? (
          ItemcardDetail?.titleNode
         ) : (
          <h3 className="mb-0 mt-0">{ItemcardDetail?.title}</h3>
         )}
         {ItemcardDetail?.description && type === 'note' && (
          <h4
           className="mb-1 mt-0 line-clamp-1"
           // eslint-disable-next-line react/no-danger
           dangerouslySetInnerHTML={{ __html: ItemcardDetail.description }}
          />
         )}
         {ItemcardDetail?.description && type !== 'note' && (
          <h4 className="mb-1 mt-0 line-clamp-1">
           {ItemcardDetail?.description}
          </h4>
         )}
         {ItemcardDetail?.updated && type === 'note' && (
          <p className="mb-0 mt-2" style={{ fontSize: '13px' }}>
           <span className="activity-meta-chip">
            <i className="fal fa-clock" />
            {formatDate?.(ItemcardDetail?.updated)}
           </span>
           {ItemcardDetail?.createdBy && (
            <span className="activity-meta-chip ms-2">
             <Avatar
              size={18}
              style={{
               // Grey for other users' (read-only) notes, primary for own notes
               backgroundColor: isReadOnlyNote
                ? '#bfbfbf'
                : 'var(--bs-primary)',
              }}
             >
              {Utils.shortName(ItemcardDetail.createdBy)}
             </Avatar>
             {ItemcardDetail.createdBy}
            </span>
           )}
           {isReadOnlyNote && (
            <span className="activity-meta-chip activity-meta-chip-muted ms-2">
             <i className="fal fa-lock" />
             Read only
            </span>
           )}
          </p>
         )}
         {ItemcardDetail?.updated && type !== 'note' && (
          <p className="mb-0 mt-2" style={{ fontSize: '13px' }}>
           <span
            style={{
             border: ' 1px solid #ccc',
             padding: ' 0 7px',
            }}
           >
            {formatDate?.(ItemcardDetail?.updated)}
           </span>
           {ItemcardDetail?.estatus && (
            <Tag
             className="ms-2"
             style={{
              color: 'var(--bs-primary)',
              borderColor: 'var(--bs-primary)',
              background: 'rgba(var(--bs-primary-rgb), 0.08)',
             }}
            >
             {ItemcardDetail.estatus}
            </Tag>
           )}
          </p>
         )}
        </div>
       }
      />
     </div>
     {from === 'email' && isTransferred && (
      <div
       onClick={(e) => {
        e.stopPropagation()
        // fetchDetail?.()
       }}
      >
       <ActivityEmailActions onClickReOrFor={fetchDetail} datas={data} />
      </div>
     )}
     {type === 'note' && (
      <div onClick={(e) => e.stopPropagation()}>
       <ActivityNoteActions
        note={rowData}
        onEdit={onEditNote}
        onDeleted={refechList}
        isTransferred={isTransferred}
       />
      </div>
     )}
    </div>
   </Card>
   {showModal &&
    buildDetailModal(
     () => setShowModal(false),
     showModal,
     type,
     refechList,
     loading,
     data,
    )}
  </>
 )
}

export default CommonItemCard
